/**
 * One row of the translations editor table.
 *
 * Local state holds the EN+DE draft so typing doesn't fire a mutation
 * per keystroke. Save is on blur — when the user tabs out, we hand the
 * current draft up to the parent which decides whether to upsert (the
 * parent skips the round-trip if values are unchanged).
 *
 * Visual cues:
 *   - missing-language inputs get an amber border so editors can spot
 *     gaps when scrolling
 *   - the row's last-updated timestamp shows under the key for context
 *
 * Kept as its own component (rather than inlined) because each row owns
 * independent draft state — co-locating that with the parent's filter
 * memo would force a re-mount on every search keystroke and lose focus.
 */
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateNumeric } from "@/lib/date-format";
import type { TranslationRow as TranslationRowType } from "@/hooks/v2/useTranslations";

type Props = {
  row: TranslationRowType;
  onSave: (next: { en: string; de: string }) => void | Promise<void>;
};

function formatRelativeShort(
  iso: string,
  labels: {
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
  },
  language?: string
): string {
  // Lightweight "2h ago" / "3d ago" formatter — keeps the row compact.
  // Falls back to the date string for anything older than 30 days.
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return labels.justNow;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}${labels.minutesAgo}`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}${labels.hoursAgo}`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}${labels.daysAgo}`;
  return formatDateNumeric(iso, language);
}

export function TranslationsRow({ row, onSave }: Props) {
  const { t, language } = useLanguage();
  const [en, setEn] = useState(row.en ?? "");
  const [de, setDe] = useState(row.de ?? "");

  // Reset local draft if the underlying row changes from outside (e.g.
  // another admin edits the same key, react-query refetches). Without this
  // the input would silently keep showing the stale local draft.
  useEffect(() => {
    setEn(row.en ?? "");
    setDe(row.de ?? "");
  }, [row.en, row.de]);

  function commit() {
    void onSave({ en, de });
  }

  const enMissing = !en.trim();
  const deMissing = !de.trim();

  return (
    <tr className="border-t hover:bg-muted/30 transition-colors">
      <td className="px-4 py-2 align-top">
        <div className="font-mono text-xs break-all">{row.key}</div>
        {row.description && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {row.description}
          </div>
        )}
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {formatRelativeShort(
            row.updated_at,
            {
              justNow: t("admin.translations.justNow", "just now"),
              minutesAgo: t("admin.translations.minutesAgo", "m ago"),
              hoursAgo: t("admin.translations.hoursAgo", "h ago"),
              daysAgo: t("admin.translations.daysAgo", "d ago"),
            },
            language
          )}
        </div>
      </td>
      <td className="px-4 py-2 align-top w-2/5">
        <Input
          value={en}
          onChange={(e) => setEn(e.target.value)}
          onBlur={commit}
          placeholder="—"
          className={cn(
            "text-sm",
            enMissing && "border-amber-300 focus-visible:ring-amber-400"
          )}
          aria-label={`${t("admin.translations.englishValueFor", "English value for")} ${row.key}`}
        />
      </td>
      <td className="px-4 py-2 align-top w-2/5">
        <Input
          value={de}
          onChange={(e) => setDe(e.target.value)}
          onBlur={commit}
          placeholder="—"
          className={cn(
            "text-sm",
            deMissing && "border-amber-300 focus-visible:ring-amber-400"
          )}
          aria-label={`${t("admin.translations.germanValueFor", "German value for")} ${row.key}`}
        />
      </td>
    </tr>
  );
}
